import { useEffect, useState } from 'react';
import { EntityModal } from '../common';
import { useDialog } from '../../context/DialogContext';
import { useCreateClientEventGuest, useUpdateClientEventGuest } from '../../hooks/usePortal';
import type { ClientGuest } from '../../api/portal';

export interface ClientGuestModalProps {
  open: boolean;
  onClose: () => void;
  eventId: string;
  /** Guest to edit; omit to add a new one. */
  guest?: ClientGuest | null;
}

/**
 * Add or edit a guest from the client portal.
 *
 * Deliberately narrower than the producer's guest form: a client says who is
 * coming and how many, while seating, meals and RSVP state stay with the
 * producer. Only rendered when the client's `canManageGuests` permission is on.
 */
export function ClientGuestModal({ open, onClose, eventId, guest }: ClientGuestModalProps) {
  const { alert } = useDialog();
  const createGuest = useCreateClientEventGuest(eventId);
  const updateGuest = useUpdateClientEventGuest(eventId);
  const isEdit = !!guest;

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [familyGroup, setFamilyGroup] = useState('');
  const [adults, setAdults] = useState(1);
  const [kids, setKids] = useState(0);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFirstName(guest?.firstName ?? '');
    setLastName(guest?.lastName ?? '');
    setEmail(guest?.email ?? '');
    setPhone(guest?.phone ?? '');
    setFamilyGroup(guest?.familyGroup ?? '');
    setAdults((guest?.maleCount ?? 0) + (guest?.femaleCount ?? 0) || 1);
    setKids(guest?.kidCount ?? 0);
    setNotes(guest?.notes ?? '');
  }, [open, guest]);

  const handleSave = async () => {
    if (!firstName.trim()) {
      await alert({ message: 'Please enter the guest’s first name.', variant: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        firstName: firstName.trim(),
        lastName: lastName.trim() || undefined,
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        familyGroup: familyGroup.trim() || undefined,
        // The portal asks for "adults", which the guest list stores split by
        // gender; put the whole party in maleCount and leave the split to the
        // producer, exactly as the public RSVP write-back does.
        maleCount: adults,
        femaleCount: 0,
        kidCount: kids,
        notes: notes.trim() || undefined,
      };
      if (isEdit && guest) {
        await updateGuest.mutateAsync({ guestId: guest.id, updates: payload });
      } else {
        await createGuest.mutateAsync(payload);
      }
      onClose();
    } catch (err) {
      await alert({
        message: err instanceof Error ? err.message : 'Failed to save the guest',
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <EntityModal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit Guest' : 'Add Guest'}
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving}
            className="px-4 py-2 text-sm text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Guest'}
          </button>
        </div>
      }
    >
      <div className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label htmlFor="cg-first" className="block text-sm font-medium text-gray-700">
              First name <span className="text-red-500">*</span>
            </label>
            <input
              id="cg-first"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <div>
            <label htmlFor="cg-last" className="block text-sm font-medium text-gray-700">
              Last name
            </label>
            <input
              id="cg-last"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <div>
            <label htmlFor="cg-email" className="block text-sm font-medium text-gray-700">
              Email
            </label>
            <input
              id="cg-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <div>
            <label htmlFor="cg-phone" className="block text-sm font-medium text-gray-700">
              Phone
            </label>
            <input
              id="cg-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <div>
            <label htmlFor="cg-family" className="block text-sm font-medium text-gray-700">
              Family group
            </label>
            <input
              id="cg-family"
              type="text"
              value={familyGroup}
              onChange={(e) => setFamilyGroup(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="cg-adults" className="block text-sm font-medium text-gray-700">
              Adults
            </label>
            <input
              id="cg-adults"
              type="number"
              min={0}
              max={100}
              value={adults}
              onChange={(e) => setAdults(Number(e.target.value))}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <div>
            <label htmlFor="cg-kids" className="block text-sm font-medium text-gray-700">
              Children
            </label>
            <input
              id="cg-kids"
              type="number"
              min={0}
              max={100}
              value={kids}
              onChange={(e) => setKids(Number(e.target.value))}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
        </div>

        <div>
          <label htmlFor="cg-notes" className="block text-sm font-medium text-gray-700">
            Notes
          </label>
          <textarea
            id="cg-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>
      </div>
    </EntityModal>
  );
}
